import basicInfoTemplate from './basicInfo.template.html';

/*******************************************************************************
 * Component: basicInfoItemContent
 *
 * This is the component to be used to manage the basic information for an item
 * in the addChangeItemDialog component.
 *
 * This component can be used as the only content for certain cases, as such,
 * some fields like for sales recording will be available here.
 *******************************************************************************/
export const basicInfoItemContentComponent = {
  require: { dialogCtrl: '^^addChangeItemDialogContent' },
  template: basicInfoTemplate,
  controller: Controller,
  controllerAs: '$biCtrl',
  transclude: {
    singleSalesAcctInfo: '?singleSalesAcctInfo'
  }
}

/* @ngInject */ function Controller(
  $element,
  $injector,
  $transclude,
  BooksSettings,
  ItemAdminService,
  SERVER
) {
  var $biCtrl = this;
  var $svcs = {
    $biCtrl: $biCtrl,
    $element: $element,
    $transclude: $transclude
  };

  Object.assign($biCtrl, {
    accountLabel: SERVER.BooksWebConstants.accountLabel, // Account Label.
    classLabel: SERVER.BooksWebConstants.classLabel, // Class Selection Label.
    categoryDialogActive: false, // Category dialog open flag.
    categoryList: ItemAdminService.getCategories(), // Item Category List data provider.
    identifyLabel: SERVER.BooksWebConstants.identifyLabel, // Identity Purchase Label.
    notesMaxLength: BooksSettings.FL_NOTES, // Max characters for description.
    showAccountNumber: SERVER.BooksWebConstants.showAcctNumber, // Show Account Number Flag.
    validators: {}, // Content Validators. Populated by csmValidators directive.

    $onInit: $injector.invoke($onInit, null, $svcs),
    addCategoryHandler: $injector.invoke(addCategoryHandler, null, $svcs),
    clearClass: $injector.invoke(clearClass, null, $svcs),
    clearVendor: $injector.invoke(clearVendor, null, $svcs),
    downloadTextbookInformation: $injector.invoke(
      downloadTextbookInformation,
      null,
      $svcs
    ),
    handleImage: $injector.invoke(handleImage, null, $svcs)
  });

  Object.defineProperties($biCtrl, {
    /*---------------------------------------------------*/
    /* Set up an accessor the item property from the     */
    /* dialogCtrl. Use an empty object for data binding. */
    /*---------------------------------------------------*/
    item: {
      get: function () {
        return $biCtrl.dialogCtrl ? $biCtrl.dialogCtrl.item : {};
      }
    },

    /*----------------------------------------------------------*/
    /* Disable the sales account disbursements button when      */
    /* this is an inventory product and a trust account is set. */
    /*----------------------------------------------------------*/
    disableDisbursements: {
      get: function () {
        return (
          $biCtrl.dialogCtrl.uiFlags.isInvProduct &&
          $biCtrl.dialogCtrl.uiFlags.forTrust
        );
      }
    },

    /*---------------------------------------------------------*/
    /* Requires an indication if the sales account is required */
    /*---------------------------------------------------------*/
    requireSalesAccount: {
      get: function () {
        return (
          $biCtrl.dialogCtrl.uiFlags.isMembership ||
          $biCtrl.dialogCtrl.uiFlags.isFee ||
          $biCtrl.dialogCtrl.uiFlags.isFine ||
          $biCtrl.dialogCtrl.uiFlags.isMembership ||
          $biCtrl.dialogCtrl.uiFlags.isDonation
        );
      }
    },

    /*--------------------------------------------------------*/
    /* Requires an indication if the sales amount is required */
    /*--------------------------------------------------------*/
    requireSaleAmount: {
      get: function () {
        return (
          $biCtrl.dialogCtrl.uiFlags.isMembership ||
          $biCtrl.dialogCtrl.uiFlags.isFee
        );
      }
    },

    /*----------------------------------------------------*/
    /* Return an indication if the barcode is to be used. */
    /*----------------------------------------------------*/
    showBarcode: {
      get: function () {
        return (
          $biCtrl.dialogCtrl.uiFlags.isEquipText ||
          $biCtrl.dialogCtrl.uiFlags.isProduct ||
          $biCtrl.dialogCtrl.uiFlags.isGroup ||
          $biCtrl.dialogCtrl.uiFlags.isMembership
        );
      }
    },

    /*------------------------------------------------------------*/
    /* Return an indication if the class selection is to be used. */
    /*------------------------------------------------------------*/
    showClass: {
      get: function () {
        return (
          SERVER.BooksWebConstants.withClass &&
          ($biCtrl.dialogCtrl.uiFlags.isBaseItem ||
            $biCtrl.dialogCtrl.uiFlags.isDonation ||
            $biCtrl.dialogCtrl.uiFlags.isGroup ||
            $biCtrl.dialogCtrl.uiFlags.isProduct)
        );
      }
    },

    /*---------------------------------------------*/
    /* Return an indication if the deposit account */
    /* selection is to be used on this component.  */
    /*---------------------------------------------*/
    showDepositAccount: {
      get: function () {
        return (
          !$biCtrl.dialogCtrl.uiFlags.isEquipText &&
          !$biCtrl.dialogCtrl.uiFlags.isProduct &&
          $biCtrl.dialogCtrl.showDeposit()
        );
      }
    },

    /*---------------------------------------*/
    /* Returns an indication if the sales    */
    /* account selector should be available. */
    /*---------------------------------------*/
    showSalesAccount: {
      get: function () {
        return (
          $biCtrl.dialogCtrl.uiFlags.showAccount &&
          !$biCtrl.dialogCtrl.uiFlags.isProduct
        );
      }
    },

    /*------------------------------------------------*/
    /* Return an indication if the sku is to be used. */
    /*------------------------------------------------*/
    showSKU: {
      get: function () {
        return (
          $biCtrl.dialogCtrl.uiFlags.isPOS ||
          $biCtrl.dialogCtrl.uiFlags.isFine
        );
      }
    }
  });
}

/*****************************************************************************
 * $onInit */
/**
 * Initialization callback function. The required dialogCtrl will be available
 * at this point for initialization purposes.
 *****************************************************************************/
/* @ngInject */ function $onInit(
  $biCtrl,
  $element,
  $transclude,
  CompilerService
) {
  return function () {
    $biCtrl.amountLabel = $biCtrl.dialogCtrl.uiFlags.isEquipText
      ? 'Value'
      : 'Amount';
    $biCtrl.barcodeLabel = $biCtrl.dialogCtrl.uiFlags.isTextbook
      ? 'ISBN'
      : 'Barcode';
    $biCtrl.dialogCtrl.tabBasicInfo.validators = $biCtrl.validators;

    /*------------------------------------------------------------------*/
    /* Insert transcluded content and add reference to this controller. */
    /*------------------------------------------------------------------*/
    CompilerService.transcludeSlots($element, { $biCtrl: this }, $transclude);
  };
}

/**************************************************************************
 * addCategoryHandler */
/**
 * Fired when the New category button is clicked. Adds a new category and
 * selects it.
 ***************************************************************************/
/* @ngInject */ function addCategoryHandler(
  $biCtrl,
  AddChangeItemCategoryDialog,
  ItemAdminService
) {
  return function () {
    /*---------------------------------*/
    /* Dialog is already active, exit. */
    /*---------------------------------*/
    if ($biCtrl.categoryDialogActive) return;

    /*---------------------------------*/
    /* Display dialog to add category. */
    /*---------------------------------*/
    $biCtrl.categoryDialogActive = true;
    AddChangeItemCategoryDialog.show()
      .then(function (newCategory) {
        /*---------------------------------------*/
        /* Reload the list and update selection. */
        /*---------------------------------------*/
        ItemAdminService.reloadCategories().then(function () {
          $biCtrl.categoryList = ItemAdminService.getCategories();
          $biCtrl.item.categoryID = newCategory.id;
        });
      })
      ['finally'](function () {
        $biCtrl.categoryDialogActive = false;
      });
  };
}

/***************************************************************************
 * clearClass */
/**
 * Clears the current class/fundraiser selection
 ***************************************************************************/
/* @ngInject */ function clearClass($biCtrl) {
  return function () {
    $biCtrl.item.classID = 0;
  };
}

/***************************************************************************
 * clearVendor */
/**
 * Clears the current vendor selection
 ***************************************************************************/
/* @ngInject */ function clearVendor($biCtrl) {
  return function () {
    $biCtrl.item.vendorID = 0;
  };
}

/***************************************************************************
 * downloadTextbookInformation */
/**
 * Get the ISBNs' Information from ISBNDB.com and load the form with
 * the data.
 ***************************************************************************/
/* @ngInject */ function downloadTextbookInformation(
  $biCtrl,
  Alert,
  BusyScreen,
  BooksSvc
) {
  return function () {
    var isbn = ($biCtrl.item.barcode || '').trim();

    /*--------------------------------*/
    /* No ISBN, notify user and exit. */
    /*--------------------------------*/
    if (!isbn) {
      Alert.error('No ISBN provided', 'Missing ISBN');
      return;
    }

    /*---------------------------*/
    /* Get Textbook information. */
    /*---------------------------*/
    BusyScreen.setBusy(true);
    BooksSvc.downloadTextbookInformation(isbn)
      .then(
        function (resp) {
          $biCtrl.item.shortDescription = resp.data.TextbookInfo.Title;
          $biCtrl.item.longDescription =
            resp.data.TextbookInfo.MLAFormatString;
          $biCtrl.item.amount = resp.data.TextbookInfo.MaxReplacementPrice;
        },

        /*----------------------------------*/
        /* Service call error, notify user. */
        /*----------------------------------*/
        function (resp) {
          Alert.error(resp.parsedResponse.message(), 'ISBN Error');
        }
      )
      ['finally'](function () {
        BusyScreen.setBusy(false);
      });
  };
}

/***************************************************************************
 * handleImage */
/**
 * Manage image button click handler. Ensure the item exists before
 * utilizing the ImageLoaderPanel.
 ***************************************************************************/
/* @ngInject */ function handleImage($biCtrl, $q, Alert, BooksTools, SERVER) {
  return function () {
    /*--------------------------------*/
    /* Ensure the item has been saved */
    /*--------------------------------*/
    $q.when(
      !$biCtrl.item.id
        ? Alert.confirm(
            'This item needs to be saved before continuing. Would you like to save now?',
            'Save Needed'
          ).then(function (resp) {
            return $biCtrl.dialogCtrl.saveItem();
          })
        : $q.resolve()
    )

      /*-----------------*/
      /* Display dialog. */
      /*-----------------*/
      .then(function () {
        BooksTools.showImageLoaderPanel(SERVER.BooksDBConstants.UIT_PRODUCT, {
          orgID: SERVER.currentUser.orgID,
          ID: $biCtrl.item.id
        });
      });
  };
}
